import { useRef, useEffect, useState, Suspense } from 'react';
import { useThree } from '@react-three/fiber';
import {
  OrbitControls,
  Environment,
  ContactShadows,
  BakeShadows,
  Preload,
  useHelper,
  Stats,
  AdaptiveDpr,
  PerformanceMonitor,
} from '@react-three/drei';
import * as THREE from 'three';
import { useScene } from '@/contexts/scene-context';
import { useData } from '@/contexts/data-context';
import SensorVisualization from './sensor-visualization';
import EnvironmentStructure from './environment-structure';
import HeatmapVisualization from './heatmap-visualization';
// import { EffectComposer, Bloom, ToneMapping, N8AO, DepthOfField, Noise, Vignette } from '@react-three/postprocessing';

export default function Scene3D() {
  const { sceneState, updateSceneState } = useScene();
  const { sensors, selectedEnvironment } = useData();
  const directionalLightRef = useRef<THREE.DirectionalLight>(null);
  const [showPerformanceStats, setShowPerformanceStats] = useState(false);
  const [dpr, setDpr] = useState(1);

  // Use helper to visualize directional light when in debug mode
  useHelper(sceneState.debugMode ? directionalLightRef : null, THREE.DirectionalLightHelper, 1, 'yellow');

  // Update camera position when it changes in state
  const { camera, gl } = useThree();

  useEffect(() => {
    if (sceneState.cameraPosition) {
      camera.position.set(...sceneState.cameraPosition);
      camera.lookAt(0, 0, 0);
    }

    // Enable shadow map for better quality
    gl.shadowMap.enabled = sceneState.enableShadows;
    gl.shadowMap.type = THREE.PCFSoftShadowMap;

    // Toggle performance stats with keyboard shortcut
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'p' && e.ctrlKey) {
        setShowPerformanceStats((prev) => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [sceneState.cameraPosition, sceneState.enableShadows, camera, gl]);

  // Get temperature sensors for heatmap
  const temperatureSensors = sensors.filter((s) => s.type === 'temperature');

  // Get environment-specific settings
  const getEnvironmentSettings = () => {
    switch (selectedEnvironment) {
      case 'datacenter':
        return {
          envPreset: 'warehouse',
          groundColor: '#333333',
          ambientIntensityMultiplier: 0.8,
          fogColor: '#111111',
          fogDensity: 0.01,
        };
      case 'greenhouse':
        return {
          envPreset: 'forest',
          groundColor: '#2d4c1e',
          ambientIntensityMultiplier: 1.2,
          fogColor: '#c9e8c0',
          fogDensity: 0.02,
        };
      case 'charging':
        return {
          envPreset: 'city',
          groundColor: '#444444',
          ambientIntensityMultiplier: 0.9,
          fogColor: '#555555',
          fogDensity: 0.005,
        };
      case 'smartbuilding':
        return {
          envPreset: 'apartment',
          groundColor: '#555555',
          ambientIntensityMultiplier: 1.0,
          fogColor: '#e0e0e0',
          fogDensity: 0.001,
        };
      case 'industrial':
        return {
          envPreset: 'warehouse',
          groundColor: '#444444',
          ambientIntensityMultiplier: 0.7,
          fogColor: '#555555',
          fogDensity: 0.015,
        };
      default:
        return {
          envPreset: 'warehouse',
          groundColor: '#444444',
          ambientIntensityMultiplier: 1.0,
          fogColor: '#555555',
          fogDensity: 0.01,
        };
    }
  };

  const envSettings = getEnvironmentSettings();

  // Get resolution multiplier based on quality setting
  const getResolutionMultiplier = () => {
    switch (sceneState.renderQuality) {
      case 'low':
        return 0.5;
      case 'medium':
        return 0.75;
      case 'high':
        return 1;
      case 'ultra':
        return 1.5;
      default:
        return 1;
    }
  };

  return (
    <>
      {/* Performance monitoring */}
      <PerformanceMonitor
        onDecline={() => {
          // Automatically reduce quality when performance declines
          if (sceneState.renderQuality === 'ultra') updateSceneState({ renderQuality: 'high' });
          else if (sceneState.renderQuality === 'high') updateSceneState({ renderQuality: 'medium' });
          else if (sceneState.renderQuality === 'medium') updateSceneState({ renderQuality: 'low' });
        }}
      />

      {/* Adaptive DPR for performance */}
      <AdaptiveDpr pixelated />

      {/* Performance Stats (hidden by default) */}
      {showPerformanceStats && <Stats />}

      {/* Lighting */}
      <ambientLight intensity={sceneState.ambientIntensity * envSettings.ambientIntensityMultiplier} />
      <directionalLight
        ref={directionalLightRef}
        position={sceneState.directionalPosition}
        intensity={sceneState.directionalIntensity}
        castShadow={sceneState.enableShadows}
        shadow-mapSize={[1024, 1024]}
        shadow-camera-left={-10}
        shadow-camera-right={10}
        shadow-camera-top={10}
        shadow-camera-bottom={-10}
        shadow-camera-near={0.1}
        shadow-camera-far={40}
        shadow-bias={-0.0001}
      />

      {/* Spot lights for additional detail */}
      <spotLight
        position={[5, 8, 0]}
        angle={0.3}
        penumbra={0.8}
        intensity={0.8}
        castShadow={sceneState.enableShadows}
        shadow-mapSize={[512, 512]}
      />

      {/* Environment */}
      <Environment preset={envSettings.envPreset as any} background={false} />

      {/* Fog for depth */}
      <fog attach="fog" args={[envSettings.fogColor, 0, 1 / envSettings.fogDensity]} />

      {/* Ground with contact shadows */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.025, 0]} receiveShadow={sceneState.enableShadows}>
        <planeGeometry args={[100, 100]} />
        <meshStandardMaterial color={envSettings.groundColor} roughness={0.9} metalness={0.1} envMapIntensity={0.2} />
      </mesh>

      {sceneState.enableShadows && (
        <ContactShadows
          position={[0, -0.01, 0]}
          opacity={0.6}
          scale={40}
          blur={2}
          far={10}
          resolution={512}
          color="#000000"
        />
      )}

      {/* Bake shadows for performance */}
      {sceneState.enableShadows && <BakeShadows />}

      {/* Environment Structure */}
      <Suspense fallback={null}>
        <EnvironmentStructure environment={selectedEnvironment} />
      </Suspense>

      {/* Heatmap Visualization */}
      {sceneState.enableHeatmap && (
        <HeatmapVisualization
          sensors={temperatureSensors}
          resolution={sceneState.renderQuality === 'low' ? 20 : sceneState.renderQuality === 'medium' ? 30 : 40}
          height={0.05}
          opacity={0.5}
        />
      )}

      {/* Sensor Visualizations */}
      <Suspense fallback={null}>
        {sensors.map((sensor) => (
          <SensorVisualization key={sensor.id} sensor={sensor} />
        ))}
      </Suspense>

      {/* Post-processing effects */}
      {/* {sceneState.enablePostProcessing && (
        <EffectComposer>
          <N8AO aoRadius={1} intensity={1} aoSamples={4} denoiseSamples={4} />
          <Bloom intensity={0.05} luminanceThreshold={0.8} luminanceSmoothing={0.9} />
          <ToneMapping
            adaptive
            resolution={256}
            middleGrey={0.6}
            maxLuminance={16.0}
            averageLuminance={1.0}
            adaptationRate={1.0}
          />
        </EffectComposer>
      )} */}

      {/* Camera Controls */}
      <OrbitControls
        enablePan={true}
        enableZoom={true}
        enableRotate={true}
        dampingFactor={0.05}
        screenSpacePanning={false}
        minDistance={2}
        maxDistance={50}
        maxPolarAngle={Math.PI / 2 - 0.1} // Prevent going below ground
      />

      {/* Preload assets for better performance */}
      <Preload all />
    </>
  );
}
